import { ArrowDown } from 'lucide-react';
import { SiteLayout } from '../components/site/SiteLayout';
import { PageHero, CheckList, SectionTitle } from '../components/site/PageHero';
import { PhoneButton } from '../components/site/PhoneButton';
import { BookingBlock } from '../components/site/BookingBlock';
import MedicareBadge from '../components/MedicareBadge';

const PARTS = [
  ['Part A', 'Hospital insurance', 'Inpatient hospital stays, skilled nursing facility care, hospice and some home health care.'],
  ['Part B', 'Medical insurance', 'Doctor visits, outpatient care, preventive services and medical equipment.'],
  ['Part C', 'Medicare Advantage', 'Private plans that bundle Part A and B, often with drug coverage and extra benefits.'],
  ['Part D', 'Prescription drugs', 'Stand-alone drug plans that help cover the cost of your prescriptions.'],
  ['Medigap', 'Supplement plans', 'Helps pay costs Original Medicare leaves behind, like copays and deductibles.'],
];

export default function Medicare() {
  return (
    <SiteLayout testid="medicare-page">
      <PageHero
        testid="medicare-hero"
        tone="blue"
        eyebrow="Medicare Insurance"
        title="Navigate Medicare with"
        accent="confidence"
        intro="Our expert consultants help you choose the right Medicare plan, understand your coverage options, and maximize your benefits. From Original Medicare to Medicare Advantage and Medigap supplements, we guide you through every decision."
        image="https://images.pexels.com/photos/7551662/pexels-photo-7551662.jpeg"
        imageAlt="Medicare insurance consultation"
        aside={<MedicareBadge size="xl" />}
      >
        <div className="space-y-8">
          <CheckList tone="blue" items={['Personalized Medicare plan selection', 'Medicare Advantage vs. Medigap comparison', 'Enrollment assistance and annual reviews', 'Prescription drug plan optimization']} />
          <div className="flex flex-wrap gap-3">
            <PhoneButton type="medicare" size="lg" testid="medicare-hero-phone" />
            <a href="#book" data-testid="medicare-hero-book-link" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 font-semibold text-white hover:border-blue-400 hover:text-blue-300">
              Book a time <ArrowDown className="w-4 h-4" />
            </a>
          </div>
        </div>
      </PageHero>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20" data-testid="medicare-parts">
        <SectionTitle kicker="The basics" title="The parts of Medicare, simply explained" sub="We walk you through each one and how they fit your health, doctors and budget." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {PARTS.map(([part, name, copy]) => (
            <div key={part} className="glass-panel rounded-3xl p-6 hover:border-blue-400/50 hover:-translate-y-1">
              <p className="font-display text-3xl font-bold text-blue-400">{part}</p>
              <p className="text-white font-semibold mt-2 mb-3">{name}</p>
              <p className="text-sm text-slate-400 leading-relaxed">{copy}</p>
            </div>
          ))}
        </div>
      </section>

      <BookingBlock kind="medicare" testid="medicare-booking" />
    </SiteLayout>
  );
}
